import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AOIDrawer } from './AOIDrawer';
import { FrontDoorTileLayer } from './FrontDoorTileLayer';

interface MapViewProps {
  onMapLoad?: (map: L.Map) => void;
  onCursorMove?: (lat: number, lon: number, zoom: number) => void;
  onAOIComplete?: (geometry: any) => void;
  isDrawing: boolean;
}

// Get saved map center from localStorage or use default
function getSavedMapCenter(): [number, number] {
  try {
    const saved = localStorage.getItem('vortex_map_center');
    if (saved) {
      const { lat, lng } = JSON.parse(saved);
      return [lat, lng];
    }
  } catch (error) {
    console.error('Error loading saved map center:', error);
  }
  return [18, -65]; // Default: Caribbean
}

// Get saved map zoom from localStorage or use default
function getSavedMapZoom(): number {
  try {
    const saved = localStorage.getItem('vortex_map_zoom');
    if (saved) {
      return parseInt(saved, 10);
    }
  } catch (error) {
    console.error('Error loading saved map zoom:', error);
  }
  return 8; // Default zoom
}

// Component to handle map events and callbacks
function MapEventHandler({ onMapLoad, onCursorMove }: Pick<MapViewProps, 'onMapLoad' | 'onCursorMove'>) {
  const map = useMap();

  useEffect(() => {
    if (onMapLoad) {
      onMapLoad(map);
    }
  }, [map, onMapLoad]);

  useMapEvents({
    mousemove: (e) => {
      if (onCursorMove) {
        onCursorMove(e.latlng.lat, e.latlng.lng, map.getZoom());
      }
    },
    zoomend: () => {
      // Update zoom level immediately after zoom animation completes
      if (onCursorMove) {
        const center = map.getCenter();
        onCursorMove(center.lat, center.lng, map.getZoom());
      }
    },
  });

  return null;
}

export function MapView({ onMapLoad, onCursorMove, onAOIComplete, isDrawing }: MapViewProps) {
  const [initialCenter] = useState<[number, number]>(getSavedMapCenter());
  const [initialZoom] = useState<number>(getSavedMapZoom());
  const [showNauticalOverlay, setShowNauticalOverlay] = useState(false);

  return (
    <div className="w-full h-full relative">
      {/* Nautical Chart Toggle */}
      <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200">
        <button
          onClick={() => setShowNauticalOverlay(!showNauticalOverlay)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            showNauticalOverlay
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
          title="Toggle nautical chart overlay (depths, buoys, navigation marks)"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          </svg>
          <span className="text-sm font-medium">
            {showNauticalOverlay ? 'Nautical On' : 'Nautical Off'}
          </span>
        </button>
      </div>

      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        {/* Sentinel-2 Cloudless Tile Layer (EOX s2maps - Free, 10m resolution) */}
        <FrontDoorTileLayer
          source="sentinel2-cloudless"
          fallbackUrl="https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2024_3857/default/g/{z}/{y}/{x}.jpg"
          attribution='Sentinel-2 cloudless &copy; <a href="https://s2maps.eu">EOX IT Services GmbH</a> (Contains modified Copernicus Sentinel data)'
          maxZoom={18}
        />

        {/* OpenSeaMap Nautical Chart Overlay */}
        {showNauticalOverlay && (
          <TileLayer
            url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://www.openseamap.org">OpenSeaMap</a> contributors'
            maxZoom={18}
            opacity={0.8}
          />
        )}

        {/* Event Handler */}
        <MapEventHandler onMapLoad={onMapLoad} onCursorMove={onCursorMove} />

        {/* AOI Drawer - must be inside MapContainer */}
        <AOIDrawer onAOIComplete={onAOIComplete} isDrawing={isDrawing} />
      </MapContainer>
    </div>
  );
}
